import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from '@/lib/i18n';
import { friendlyMessage } from '@/lib/errors';
import { Button, Checkbox } from '@/components/ui';
import { ProductSelector } from '@/features/catalog/components/ProductSelector';
import { EventSearchSelect } from '@/features/cms/components/EventSearchSelect';
import { adminFetch } from '@/lib/apiClient';
import { ApiClientError } from '@/lib/http-core';
import { nationalityOptions } from '@/lib/countries/nationalityOptions';
import {
  EXPORT_COLUMN_KEYS,
  COLLECTION_LEVEL_COLUMNS,
  exportColumnLabel,
  ITEM_LEVEL_COLUMNS,
  ORDER_LEVEL_COLUMNS,
  type ExportColumnDef,
} from '../lib/exportColumns';
import {
  appendOrderFilterParams,
  type OrderFilterValues,
} from '../lib/orderFilterParams';
import { useExportColumnSelection } from '@/stores/useExportColumnSelection';
import { criteriaFromQuery, ORDER_EXPORT_JOBS_PATH, toOrderExportError, OrderExportError } from '../exportJobs/lib';
import { useRequestOrderExportMutation } from '../exportJobs/useOrderExportJobs';
import { DEFAULT_ROWS_PER_FILE, ROWS_PER_FILE_OPTIONS } from '../exportJobs/types';
import { useOrderExportTemplatesQuery } from '../exportTemplates/useOrderExportTemplates';
import { useExportCollectionLabelsQuery } from '../exportTemplates/useExportCollectionLabels';

/**
 * 주문 엑셀 export 컬럼 선택 localStorage 키. 모달별 고유 키로 다른 export 모달과 분리한다.
 * 항목(컬럼) 선택만 영속하며, 기간/대상(상품·이벤트·국적) 필터는 매번 새로 시작한다.
 */
const ORDER_EXPORT_COLUMNS_STORAGE_KEY = 'order-export-columns';

/**
 * QA-535 [추가입력 엑셀 다운로드] — 같은 주문 엑셀을 추가입력 위주 칸으로 연다(목록 검색 조건이 그대로 실린다).
 * 칸 선택은 일반 엑셀과 따로 기억한다. 처음엔 주문번호·주문일시·주문자명·상품명·상품코드·추가입력 전체.
 */
const COLLECTION_EXPORT_COLUMNS_STORAGE_KEY = 'order-collection-export-columns';
export const COLLECTION_EXPORT_DEFAULT_COLUMNS: ReadonlyArray<string> = [
  'orderNo',
  'orderedAt',
  'ordererName',
  'productName',
  'productCode',
  'productCollectionInputs',
];

interface OrderExportDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * ID63: 상단 검색툴바의 현재 필터. export 가 "화면에서 본 결과"와 1:1이 되도록
   * 다이얼로그 자체 필터(기간/상품/이벤트/국적)와 병합해 함께 전송한다.
   */
  listFilters?: OrderFilterValues;
  /** collection — QA-535 추가입력 엑셀(제목·기본 칸만 다르고 조건·요청 방식은 일반 엑셀과 같다). */
  mode?: 'all' | 'collection';
}

const todayFilename = (): string => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `orders_${y}${m}${d}.xlsx`;
};

const inputStyle = {
  background: 'var(--surface-1)',
  border: '1px solid var(--divider-soft)',
  color: 'var(--content-default)',
} as const;

export const OrderExportDialog = ({ open, onClose, listFilters, mode = 'all' }: OrderExportDialogProps) => {
  const isCollection = mode === 'collection';
  const { t, i18n } = useTranslation('orders');
  // 국적 필터 옵션 — 전체 ISO 국가목록(주요국 우선), 선두에 '전체'.
  const nationalityFilterOptions = useMemo(
    () => nationalityOptions(i18n.language, { allLabel: t('orders.export.filter.nationalityAll') }),
    [i18n.language, t],
  );
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [productId, setProductId] = useState<string | null>(null);
  const [eventId, setEventId] = useState<string | null>(null);
  const [nationality, setNationality] = useState('');
  // ID29: default = all columns selected (BE 기본도 전체).
  // 2026-06-20: 항목(컬럼) 선택을 localStorage 에 영속(모달 재오픈/세션 간 직전 선택 유지).
  const { columns, toggleColumn, selectAll, clearAll } = useExportColumnSelection(
    isCollection ? COLLECTION_EXPORT_COLUMNS_STORAGE_KEY : ORDER_EXPORT_COLUMNS_STORAGE_KEY,
    EXPORT_COLUMN_KEYS,
    isCollection ? COLLECTION_EXPORT_DEFAULT_COLUMNS : EXPORT_COLUMN_KEYS,
  );
  const title = isCollection ? t('orders.export.collectionTitle', '추가입력 엑셀 다운로드') : t('orders.export.title');
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 다운로드 요청(비동기 생성) — 파일당 행 수, 요청 성공 안내. Idempotency-Key 는 대화상자를 열 때마다 새로
  // 만들고 재시도에는 같은 키를 쓴다(버튼을 두 번 눌러도 1건). 성공 후에는 다음 요청을 위해 새 키.
  const [rowsPerFile, setRowsPerFile] = useState<number>(DEFAULT_ROWS_PER_FILE);
  // 고도몰처럼 [비밀번호 설정] — 다운로드 요청에만(바로 받기는 비밀번호 없음). 서버에 남지 않는다.
  const [usePassword, setUsePassword] = useState(false);
  const [password, setPassword] = useState('');
  const passwordOk = !usePassword || (password.length >= 8 && password.length <= 64);
  const [requested, setRequested] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const requestExport = useRequestOrderExportMutation();
  useEffect(() => {
    if (open) {
      setRequested(false);
      setError(null);
      setIdempotencyKey(crypto.randomUUID());
    }
  }, [open]);

  // QA-433 — 저장된 다운로드 양식. 고르면 그 양식의 항목·순서로 내려받는다(직접 고른 항목은 그대로 보존).
  const templatesQuery = useOrderExportTemplatesQuery(open);
  const templates = templatesQuery.data ?? [];
  const [templateId, setTemplateId] = useState('');
  const template = templates.find((tp) => tp.templateId === templateId) ?? null;

  // QA-468 — 상품 추가입력 라벨 칸(productCollectionInput:<라벨>). 직접 고르기에서만, 고른 순서대로 고정 칸 뒤에 붙인다.
  const labelsQuery = useExportCollectionLabelsQuery(open);
  const collectionLabels = labelsQuery.data ?? [];
  const [labelKeys, setLabelKeys] = useState<string[]>([]);
  const toggleLabel = (key: string, on: boolean) => {
    // 라벨을 고르면 "추가입력 전체"는 푼다 — 같이 고르면 그 라벨 칸이 두 번 나온다.
    if (on) toggleColumn('productCollectionInputs', false);
    setLabelKeys((prev) => (on ? (prev.includes(key) ? prev : [...prev, key]) : prev.filter((k) => k !== key)));
  };

  const selectedSet = useMemo(() => new Set(columns), [columns]);
  const allSelected = columns.length === EXPORT_COLUMN_KEYS.length;
  const noneSelected = template ? template.columns.length === 0 : columns.length === 0 && labelKeys.length === 0;

  if (!open) return null;

  /** 목록 필터 + 대화상자 조건 — 즉시 다운로드(쿼리)와 다운로드 요청(본문)이 같은 조건을 쓴다. */
  const buildQuery = (): URLSearchParams => {
      const qs = new URLSearchParams();
      // ID63: merge current list-toolbar filters first.
      if (listFilters) appendOrderFilterParams(qs, listFilters);
      // Dialog-local filters (override / add).
      if (dateFrom) qs.set('dateFrom', dateFrom);
      if (dateTo) qs.set('dateTo', dateTo);
      if (nationality) qs.set('nationality', nationality);
      if (productId) qs.set('productId', productId);
      if (eventId) qs.set('eventId', eventId);
      // ID29: emit selected columns only when a strict subset; full set ⇒ omit
      // (BE treats empty/all as "all columns").
      if (template) {
        // 양식 순서 그대로 — columnOrder=REQUESTED 면 BE 가 요청 순서로 칸을 낸다(전달 문서).
        template.columns.forEach((key) => qs.append('columns', key));
        qs.set('columnOrder', 'REQUESTED');
        qs.set('templateId', template.templateId);
      } else if ((!allSelected && columns.length > 0) || labelKeys.length > 0) {
        // 라벨 칸을 고르면 고정 칸도 명시해 보낸다(생략 = 전체라 라벨만 따로 붙일 수 없다).
        columns.forEach((key) => qs.append('columns', key));
        labelKeys.forEach((key) => qs.append('columns', key));
      }
      return qs;
  };

  /** 400(기간 초과·검증)·404·409 는 BE 사유를 그대로, 그 밖에는 상태별 공통 문구. */
  const messageOf = (err: unknown): string =>
    err instanceof OrderExportError && err.showBeMessage
      ? err.message
      : friendlyMessage(
          err instanceof OrderExportError
            ? new ApiClientError({ code: err.code ?? 'ORDER_EXPORT_FAILED', message: '' }, { status: err.status })
            : err,
          t('orders.export.error'),
        );

  const handleRequest = () => {
    setError(null);
    const criteria = criteriaFromQuery(buildQuery());
    criteria.rowsPerFile = rowsPerFile;
    if (usePassword) criteria.password = password;
    requestExport.mutate(
      { criteria, idempotencyKey },
      {
        onSuccess: () => {
          setRequested(true);
          setIdempotencyKey(crypto.randomUUID());
        },
        onError: (err) => setError(messageOf(err)),
      },
    );
  };

  const handleDownload = async () => {
    setError(null);
    setDownloading(true);
    try {
      const queryString = buildQuery().toString();
      const url = `/api/admin/v1/orders/export${queryString ? '?' + queryString : ''}`;
      // adminFetch — 401 시 토큰 갱신 후 1회 재시도. raw fetch 로 두면 긴 화면 체류 뒤
      // access token 이 만료됐을 때 갱신 없이 401 로 하드 실패한다(apiClient.ts adminFetch 주석).
      const res = await adminFetch(url);
      if (!res.ok) {
        // 400 ORDER_EXPORT_RANGE_TOO_LARGE(3개월 초과·시작일 없음)·COMMON_VALIDATION_FAILED 는 BE 사유를 그대로,
        // 그 밖에는 상태별 공통 문구(raw "export failed: 503" 노출 방지 — ProductExcelDialog 와 동일).
        throw await toOrderExportError(res);
      }
      const blob = await res.blob();
      const link = document.createElement('a');
      const objectUrl = URL.createObjectURL(blob);
      link.href = objectUrl;
      link.download = todayFilename();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
      onClose();
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setDownloading(false);
    }
  };
  const busy = downloading || requestExport.isPending;

  const renderColumnCheckbox = (col: ExportColumnDef) => (
    <label
      key={col.key}
      htmlFor={`export-col-${col.key}`}
      className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-xs"
      style={{ color: 'var(--content-default)' }}
    >
      <Checkbox
        id={`export-col-${col.key}`}
        checked={selectedSet.has(col.key)}
        onCheckedChange={(c) => toggleColumn(col.key, c === true)}
      />
      <span className="min-w-0 truncate">{t(col.labelKey)}</span>
    </label>
  );

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-export-mode={mode}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'var(--scrim-modal)' }}
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-xl shadow-[var(--elevation-3)]"
        style={{ background: 'var(--surface-0)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          className="border-b px-6 py-4 text-lg font-semibold"
          style={{ color: 'var(--content-strong)', borderColor: 'var(--divider-soft)' }}
        >
          {title}
        </h2>

        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
          {isCollection ? (
            <p
              className="rounded-md px-3 py-2 text-[12px]"
              style={{ background: 'var(--status-info-bg)', color: 'var(--status-info-fg)' }}
              data-testid="order-collection-export-note"
            >
              {t(
                'orders.export.collectionNote',
                '주문 목록에서 검색한 주문만, 상품별 추가입력(입력옵션)과 함께 받습니다. 받을 칸은 아래에서 바꿀 수 있습니다.',
              )}
            </p>
          ) : null}
          {/* 기간 설정 */}
          <fieldset
            className="rounded-xl p-4"
            style={{ border: '1px solid var(--divider-soft)' }}
          >
            <legend
              className="px-1 text-xs font-semibold"
              style={{ color: 'var(--content-strong)' }}
            >
              {t('orders.export.group.period')}
            </legend>
            <div className="grid grid-cols-2 gap-3">
              <label className="grid gap-1 text-xs" style={{ color: 'var(--content-muted)' }}>
                {t('orders.export.filter.dateFrom')}
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="rounded-lg px-3 py-2 text-sm"
                  style={inputStyle}
                />
              </label>
              <label className="grid gap-1 text-xs" style={{ color: 'var(--content-muted)' }}>
                {t('orders.export.filter.dateTo')}
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="rounded-lg px-3 py-2 text-sm"
                  style={inputStyle}
                />
              </label>
            </div>
            <p className="mt-2 text-[11px]" style={{ color: 'var(--content-muted)' }}>
              {t(
                'orders.export.periodHint',
                '다운로드 요청은 최대 12개월, 바로 받기는 최대 3개월까지입니다. 비우면 주문 목록의 기간을 씁니다.',
              )}
            </p>
            <label className="mt-3 grid gap-1 text-xs" style={{ color: 'var(--content-muted)' }}>
              {t('orders.export.rowsPerFile', '파일당 행 수')}
              <select
                value={rowsPerFile}
                onChange={(e) => setRowsPerFile(Number(e.target.value))}
                className="rounded-lg px-3 py-2 text-sm"
                style={inputStyle}
                data-testid="order-export-rows-per-file"
              >
                {ROWS_PER_FILE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {t('orders.export.rowsPerFileOption', {
                      count: n / 10_000,
                      defaultValue: '{{count}}만 행',
                    })}
                  </option>
                ))}
              </select>
            </label>
            <label className="mt-3 inline-flex cursor-pointer items-center gap-2 text-xs" style={{ color: 'var(--content-default)' }}>
              <input
                type="checkbox"
                checked={usePassword}
                onChange={(e) => {
                  setUsePassword(e.target.checked);
                  if (!e.target.checked) setPassword('');
                }}
                data-testid="order-export-use-password"
              />
              {t('orders.export.usePassword', '비밀번호 설정')}
            </label>
            {usePassword ? (
              <div className="mt-2 grid gap-1">
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  maxLength={64}
                  autoComplete="new-password"
                  placeholder={t('orders.export.passwordPlaceholder', '8~64자')}
                  className="rounded-lg px-3 py-2 text-sm"
                  style={inputStyle}
                  aria-label={t('orders.export.password', '엑셀 비밀번호')}
                  data-testid="order-export-password"
                />
                <p className="text-[11px]" style={{ color: passwordOk || password === '' ? 'var(--content-muted)' : 'var(--signal-danger)' }}>
                  {t(
                    'orders.export.passwordHint',
                    '엑셀을 열 때 이 비밀번호를 묻습니다(8~64자). 서버에 저장하지 않으니 잊으면 다시 요청해야 합니다. 비밀번호는 [다운로드 요청]에만 적용됩니다.',
                  )}
                </p>
              </div>
            ) : null}
          </fieldset>

          {/* 대상 좁히기 (상품/이벤트/국적) */}
          <fieldset
            className="rounded-xl p-4"
            style={{ border: '1px solid var(--divider-soft)' }}
          >
            <legend
              className="px-1 text-xs font-semibold"
              style={{ color: 'var(--content-strong)' }}
            >
              {t('orders.export.group.scope')}
            </legend>
            <div className="grid gap-3">
              <div className="grid gap-1 text-xs" style={{ color: 'var(--content-muted)' }}>
                <span>{t('orders.export.filter.productId')}</span>
                <ProductSelector
                  value={productId}
                  onChange={setProductId}
                  placeholder={t('orders.export.filter.productIdPlaceholder')}
                />
              </div>
              <div className="grid gap-1 text-xs" style={{ color: 'var(--content-muted)' }}>
                <span>{t('orders.export.filter.eventId')}</span>
                <EventSearchSelect
                  value={eventId}
                  onChange={setEventId}
                  placeholder={t('orders.export.filter.eventIdPlaceholder')}
                />
              </div>
              <label className="grid gap-1 text-xs" style={{ color: 'var(--content-muted)' }}>
                {t('orders.export.filter.nationality')}
                <select
                  value={nationality}
                  onChange={(e) => setNationality(e.target.value)}
                  className="rounded-lg px-3 py-2 text-sm"
                  style={inputStyle}
                >
                  {nationalityFilterOptions.map((opt) => (
                    <option key={opt.value || 'all'} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </fieldset>

          {/* ID29: 항목(컬럼) 선택 */}
          <fieldset
            className="rounded-xl p-4"
            style={{ border: '1px solid var(--divider-soft)' }}
          >
            <legend
              className="px-1 text-xs font-semibold"
              style={{ color: 'var(--content-strong)' }}
            >
              {t('orders.export.group.columns')}
            </legend>
            {templates.length > 0 ? (
              <label className="mb-3 grid gap-1 text-xs" style={{ color: 'var(--content-muted)' }}>
                {t('orders.exportTemplates.pick', '다운로드 양식')}
                <select
                  value={templateId}
                  onChange={(e) => setTemplateId(e.target.value)}
                  className="rounded-lg px-3 py-2 text-sm"
                  style={inputStyle}
                  data-testid="order-export-template"
                >
                  <option value="">{t('orders.exportTemplates.pickNone', '직접 고르기')}</option>
                  {templates.map((tp) => (
                    <option key={tp.templateId} value={tp.templateId}>
                      {tp.name} ({tp.columns.length})
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            {template ? (
              <ol className="flex flex-wrap gap-1.5 text-[12px]" data-testid="order-export-template-columns">
                {template.columns.map((key, i) => {
                  return (
                    <li key={key} className="rounded-full px-2 py-0.5" style={{ background: 'var(--surface-1)', color: 'var(--content-default)' }}>
                      {i + 1}. {exportColumnLabel(t, key)}
                    </li>
                  );
                })}
              </ol>
            ) : (
            <>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs" style={{ color: 'var(--content-muted)' }}>
                {t('orders.export.columns.selectedCount', { count: columns.length })}
              </span>
              <div className="flex gap-3 text-xs">
                <button
                  type="button"
                  onClick={selectAll}
                  disabled={allSelected}
                  className="underline disabled:no-underline disabled:opacity-40"
                  style={{ color: 'var(--content-muted)' }}
                >
                  {t('orders.export.columns.selectAll')}
                </button>
                <button
                  type="button"
                  onClick={clearAll}
                  disabled={noneSelected}
                  className="underline disabled:no-underline disabled:opacity-40"
                  style={{ color: 'var(--content-muted)' }}
                >
                  {t('orders.export.columns.clearAll')}
                </button>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <p
                  className="mb-1 text-[11px] font-medium uppercase tracking-wide"
                  style={{ color: 'var(--content-muted)' }}
                >
                  {t('orders.export.columns.orderLevel')}
                </p>
                <div className="grid grid-cols-2 gap-x-2 sm:grid-cols-3">
                  {ORDER_LEVEL_COLUMNS.map(renderColumnCheckbox)}
                </div>
              </div>
              <div>
                <p
                  className="mb-1 text-[11px] font-medium uppercase tracking-wide"
                  style={{ color: 'var(--content-muted)' }}
                >
                  {t('orders.export.columns.itemLevel')}
                </p>
                <div className="grid grid-cols-2 gap-x-2 sm:grid-cols-3">
                  {ITEM_LEVEL_COLUMNS.map(renderColumnCheckbox)}
                </div>
              </div>
              {/* QA-468 — 상품 추가입력: 전체(기간 내 모든 라벨) 또는 라벨별(국적·이벤트 옵션 등, 최근 90일 많이 쓰인 순). */}
              <div data-testid="order-export-collection-group">
                <p
                  className="mb-1 text-[11px] font-medium uppercase tracking-wide"
                  style={{ color: 'var(--content-muted)' }}
                >
                  {t('orders.export.columns.collectionLevel', '상품 추가입력')}
                </p>
                <div className="grid grid-cols-2 gap-x-2 sm:grid-cols-3">
                  {COLLECTION_LEVEL_COLUMNS.map(renderColumnCheckbox)}
                  {collectionLabels.map((l) => (
                    <label
                      key={l.key}
                      htmlFor={`export-label-${l.key}`}
                      className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-xs"
                      style={{ color: 'var(--content-default)' }}
                      title={t('orders.export.columns.labelCount', { count: l.count, defaultValue: '최근 90일 {{count}}건' })}
                    >
                      <Checkbox
                        id={`export-label-${l.key}`}
                        checked={labelKeys.includes(l.key)}
                        onCheckedChange={(c) => toggleLabel(l.key, c === true)}
                      />
                      <span className="min-w-0 truncate">{l.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            {noneSelected ? (
              <p className="mt-2 text-xs" style={{ color: 'var(--status-warn-fg)' }}>
                {t('orders.export.columns.noneHint')}
              </p>
            ) : null}
            </>
            )}
            <Link to="/orders/export-templates" className="mt-2 inline-block text-xs underline" style={{ color: 'var(--content-muted)' }} onClick={onClose}>
              {t('orders.exportTemplates.manage', '다운로드 양식 관리')}
            </Link>
          </fieldset>

          {error && (
            <p role="alert" className="text-xs" style={{ color: 'var(--status-danger-fg)' }}>
              {error}
            </p>
          )}
          {requested ? (
            <div
              role="status"
              className="rounded-lg px-3 py-2 text-[13px]"
              style={{ background: 'var(--status-success-bg)', color: 'var(--status-success-fg)' }}
              data-testid="order-export-requested"
            >
              {t('orders.export.requested', '요청했습니다. 엑셀 다운로드 내역에서 받으실 수 있습니다.')}{' '}
              <Link to={ORDER_EXPORT_JOBS_PATH} className="font-medium underline" onClick={onClose}>
                {t('orders.export.goToJobs', '다운로드 내역으로 가기')}
              </Link>
            </div>
          ) : null}
        </div>

        <div
          className="flex justify-end gap-2 border-t px-6 py-4"
          style={{ borderColor: 'var(--divider-soft)' }}
        >
          <Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={busy}>
            {requested ? t('orders.export.close', '닫기') : t('orders.export.cancel')}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => void handleDownload()}
            // 바로 받기는 비밀번호를 걸 수 없다 — 비밀번호를 쓰면 [다운로드 요청]으로.
            disabled={busy || noneSelected || usePassword}
            title={usePassword ? t('orders.export.passwordRequestOnly', '비밀번호를 걸려면 [다운로드 요청]을 쓰세요.') : undefined}
            data-testid="order-export-download-now"
          >
            {downloading ? t('orders.export.downloading') : t('orders.export.downloadNow', '바로 받기 (3개월 이내)')}
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleRequest}
            disabled={busy || noneSelected || !passwordOk}
            data-testid="order-export-request"
          >
            {requestExport.isPending ? t('orders.export.requesting', '요청 중…') : t('orders.export.request', '다운로드 요청')}
          </Button>
        </div>
      </div>
    </div>
  );
};
